import { UNREACHABLE } from "@colossus/api-client";
import type { ConsoleClient, components } from "@colossus/api-client";
import { attempt } from "../admin/load.ts";
import { ApiError } from "../api.ts";
import { awaitingCheckout } from "../dashboard.ts";
import type { DashboardTenant } from "../dashboard.ts";
import { takeFresh } from "../fresh.ts";
import { consoleKey, failureOf, queryClient } from "../query.ts";
import { perReader } from "../reader.ts";

export interface Workspace {
  readonly tenants: readonly DashboardTenant[];
  readonly failure: { readonly status: number | null } | null;
}

type ListedTenant = components["schemas"]["TenantView"];

const LISTED = consoleKey("/api/tenants");

export const listedTenant = (tenantId: string): ListedTenant | undefined =>
  queryClient()
    .getQueryData<readonly ListedTenant[]>(LISTED)
    ?.find((tenant) => tenant.tenantId === tenantId);

export const forgetWorkspace = (): void => {
  queryClient().removeQueries({ queryKey: LISTED, exact: true });
};

const deletedNow = perReader(() => new Set<string>());

export const markDeleted = (tenantId: string): void => {
  deletedNow().add(tenantId);
  forgetWorkspace();
};

export const deletedHere = (tenantId: string): boolean => deletedNow().has(tenantId);

export const loadWorkspace = async (
  client: ConsoleClient,
  { refresh = false, including = null }: { refresh?: boolean; including?: string | null } = {},
): Promise<Workspace> => {
  const held = queryClient().getQueryData<readonly ListedTenant[]>(LISTED);
  const missing =
    including !== null &&
    held !== undefined &&
    !held.some((tenant) => tenant.tenantId === including);
  const failed = queryClient().getQueryState(LISTED)?.status === "error";
  const reread = refresh && !takeFresh(LISTED);
  const tenants = await queryClient()
    .fetchQuery({
      queryKey: LISTED,
      queryFn: async (): Promise<readonly ListedTenant[]> => {
        const result = await attempt(client.call("get", "/api/tenants"));
        if (!result.ok) throw new ApiError("/api/tenants", result);
        return result.data;
      },
      staleTime: reread || missing || failed ? 0 : Infinity,
    })
    .then(
      (data) => ({ ok: true as const, data }),
      (thrown: unknown) => {
        const failure = failureOf(thrown);
        return { ok: false as const, status: failure === null ? UNREACHABLE : failure.status };
      },
    );

  const read: Workspace = {
    tenants: tenants.ok
      ? tenants.data.map((tenant) => ({
          tenantId: tenant.tenantId,
          botName: tenant.botName,
          sharedBot: tenant.sharedBot,
          guildId: tenant.guildId,
          guildName: tenant.guildName,
          guildIconUrl: tenant.guildIconUrl,
          guildBadge: tenant.guildBadge,
          status: deletedHere(tenant.tenantId) ? "deleted" : tenant.status,
          owner: tenant.owner,
          botAppId: tenant.botAppId,
          awaitingCheckout: awaitingCheckout({
            sharedBot: tenant.sharedBot,
            botAppId: tenant.botAppId,
            status: tenant.status,
            grants: tenant.entitlement.grants,
            awaitingSharedBot: tenant.awaitingSharedBot === true,
          }),
        }))
      : [],
    failure: tenants.ok ? null : { status: tenants.status === UNREACHABLE ? null : tenants.status },
  };
  return read;
};
